import { ArrowPathIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type {
    OverviewActivityItem,
    OverviewAttentionItem,
    OverviewTopDebtor,
    OverviewTopPractice
} from '../../../services/billing';
import { formatDoctorLine } from '../../accounts/practices/practiceFormatting';
import { formatMoneyDollars } from '../../../utils/money';

// Non-chart panels of the billing overview: ranked practices, top debtors,
// needs-attention queue and the recent billing-event feed.

// ── Shared ranked-row heading ────────────────────────────────────────────

interface RankedRowHeadingProps {
    name: string;
    doctorName: string | null;
    amount: string;
    invoices: number;
}

// Practice name over its doctor line, with the money right-aligned - the same
// stacked identity pattern the Accounts practices table uses.
function RankedRowHeading({ name, doctorName, amount, invoices }: RankedRowHeadingProps) {
    const doctorLine = formatDoctorLine(doctorName);
    return (
        <div className="flex items-start justify-between gap-3 mb-1.5">
            <div className="min-w-0">
                <p className="text-[13px] text-gray-700 truncate">{name}</p>
                {doctorLine && <p className="text-[11px] text-gray-400 truncate">{doctorLine}</p>}
            </div>
            <span className="text-[13px] font-semibold text-gray-900 tabular-nums shrink-0">
                {formatMoneyDollars(amount)}
                <span className="text-gray-400 font-normal"> · {invoices} inv</span>
            </span>
        </div>
    );
}

// ── Top practices ────────────────────────────────────────────────────────

export function TopPracticesList({ items }: { items: OverviewTopPractice[] }) {
    if (items.length === 0) {
        return (
            <p className="text-sm text-gray-400 py-10 text-center">
                No invoices in the last 90 days.
            </p>
        );
    }
    const max = Math.max(...items.map((p) => Number(p.amount)));
    return (
        <div className="flex flex-col gap-3">
            {items.map((practice) => (
                <div key={practice.practiceId} className="min-w-0">
                    <RankedRowHeading
                        name={practice.name}
                        doctorName={practice.doctorName}
                        amount={practice.amount}
                        invoices={practice.invoices}
                    />
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div
                            className="h-full rounded-full bg-gray-800"
                            style={{
                                width: `${max > 0 ? (Number(practice.amount) / max) * 100 : 0}%`
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

// ── Top debtors ──────────────────────────────────────────────────────────

// Bar hues mirror the A/R aging chart: teal for not yet due, rose for past due.
const DEBTOR_OVERDUE_CLASS = 'bg-rose-600';
const DEBTOR_CURRENT_CLASS = 'bg-teal-600';

export function TopDebtorsList({ items }: { items: OverviewTopDebtor[] }) {
    if (items.length === 0) {
        return <p className="text-sm text-gray-400 py-10 text-center">Nothing outstanding.</p>;
    }
    const max = Math.max(...items.map((p) => Number(p.amount)));
    return (
        <div className="flex flex-col gap-3">
            {items.map((practice) => {
                const amount = Number(practice.amount);
                const overdue = Number(practice.overdue);
                // Two segments inside one bar whose length is the practice's
                // share of the largest balance: overdue first, then not-yet-due.
                const barPct = max > 0 ? (amount / max) * 100 : 0;
                const overduePct = amount > 0 ? (overdue / amount) * 100 : 0;
                return (
                    <div key={practice.practiceId} className="min-w-0">
                        <RankedRowHeading
                            name={practice.name}
                            doctorName={practice.doctorName}
                            amount={practice.amount}
                            invoices={practice.invoices}
                        />
                        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                            <div
                                className="flex h-full rounded-full overflow-hidden"
                                style={{ width: `${barPct}%` }}
                            >
                                <div
                                    className={`h-full ${DEBTOR_OVERDUE_CLASS}`}
                                    style={{ width: `${overduePct}%` }}
                                />
                                <div
                                    className={`h-full ${DEBTOR_CURRENT_CLASS}`}
                                    style={{ width: `${100 - overduePct}%` }}
                                />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ── Needs attention ──────────────────────────────────────────────────────

const ATTENTION_STYLES: Record<
    OverviewAttentionItem['kind'],
    { icon: React.ReactNode; cls: string }
> = {
    error: { icon: <ExclamationTriangleIcon className="h-4 w-4" />, cls: 'bg-red-50 text-red-600' },
    expiring: { icon: <ClockIcon className="h-4 w-4" />, cls: 'bg-yellow-50 text-yellow-700' },
    pending: { icon: <ArrowPathIcon className="h-4 w-4" />, cls: 'bg-blue-50 text-blue-600' }
};

interface NeedsAttentionListProps {
    items: OverviewAttentionItem[];
    onOpen: (item: OverviewAttentionItem) => void;
}

export function NeedsAttentionList({ items, onOpen }: NeedsAttentionListProps) {
    if (items.length === 0) {
        return (
            <p className="text-sm text-gray-400 py-8 text-center">
                Nothing needs attention. All clear.
            </p>
        );
    }
    return (
        <div className="flex flex-col">
            {items.map((item) => {
                const style = ATTENTION_STYLES[item.kind];
                return (
                    <div
                        key={`${item.entityType}-${item.entityId}`}
                        className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-b-0"
                    >
                        <span
                            className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${style.cls}`}
                        >
                            {style.icon}
                        </span>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-800 truncate">
                                {item.title}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{item.sub}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => onOpen(item)}
                            className={`shrink-0 text-[13px] font-semibold px-3 h-8 rounded-lg border transition-colors ${
                                item.kind === 'error'
                                    ? 'border-red-200 text-red-600 hover:bg-red-50'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            Review
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

// ── Recent activity ──────────────────────────────────────────────────────

const EVENT_LABELS: Record<string, string> = {
    created: 'created',
    claimed: 'claimed',
    invoice_created: 'invoiced',
    po_created: 'created PO for',
    status_synced: 'synced',
    document_stored: 'stored document for',
    email_sent: 'emailed',
    retried: 'retried',
    voided: 'voided',
    canceled: 'canceled',
    charge_invoiced: 'invoiced',
    manual_trigger: 'updated',
    po_placed: 'placed on the vendor site',
    po_shipped: 'shipped',
    po_received: 'received',
    po_line_updated: 'updated a line on',
    po_billed: 'recorded a vendor invoice on',
    po_paid: 'paid',
    po_disputed: 'disputed',
    po_canceled: 'canceled PO',
    expected_cost_imputed: 'imputed expected cost on',
    error: 'failed on'
};

// Mirrors poStatusDisplay: an unmapped type is named as unhandled rather than
// dropped into the sentence as though it were a verb.
function eventLabel(eventType: string): string {
    return EVENT_LABELS[eventType] ?? `did "${eventType.replace(/_/g, ' ')}" on`;
}

function actorLabel(item: OverviewActivityItem): string {
    if (item.actor === 'system') return 'System';
    if (item.actor.startsWith('cron:')) return 'Cron';
    return item.actorName ?? 'User';
}

function fmtWhen(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}

interface RecentActivityListProps {
    items: OverviewActivityItem[];
    onOpen: (item: OverviewActivityItem) => void;
}

export function RecentActivityList({ items, onOpen }: RecentActivityListProps) {
    if (items.length === 0) {
        return <p className="text-sm text-gray-400 py-8 text-center">No billing activity yet.</p>;
    }
    return (
        <div className="flex flex-col">
            {items.map((item) => (
                <div
                    key={item.id}
                    className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-b-0"
                >
                    <span className="inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 shrink-0">
                        {actorLabel(item)}
                    </span>
                    <p className="text-sm text-gray-600 min-w-0 flex-1 truncate">
                        {eventLabel(item.eventType)}{' '}
                        <button
                            type="button"
                            onClick={() => onOpen(item)}
                            className="font-medium text-gray-900 hover:underline underline-offset-2"
                        >
                            {item.entityType} {item.entityRef ?? ''}
                        </button>
                    </p>
                    <span className="text-xs text-gray-400 shrink-0">
                        {fmtWhen(item.occurredAt)}
                    </span>
                </div>
            ))}
        </div>
    );
}
